import React, { useState } from 'react';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useAppDispatch } from '../../hooks';
import { showToast } from '../../store/slices/toastSlice';

interface BulkUploadResult {
  success: number;
  failed: number;
  errors: string[];
}

interface BulkOrganizationUploadProps {
  onClose: () => void;
  onSuccess: () => void;
}

const BulkOrganizationUpload: React.FC<BulkOrganizationUploadProps> = ({ onClose, onSuccess }) => {
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<BulkUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a CSV file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/organizations/bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data);
      if (response.data.success > 0) {
        dispatch(showToast({
          message: `Successfully created ${response.data.success} organizations${response.data.failed > 0 ? `, ${response.data.failed} failed` : ''}`,
          type: 'success'
        }));
        onSuccess();
      } else if (response.data.failed > 0) {
        dispatch(showToast({
          message: `Failed to create ${response.data.failed} organizations`,
          type: 'error'
        }));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload organizations');
      dispatch(showToast({
        message: err.response?.data?.detail || 'Failed to upload organizations',
        type: 'error'
      }));
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['name', 'domain', 'billing_email', 'technical_contact_email', 'max_users', 'input_token_limit', 'output_token_limit'];
    const csvContent = headers.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'organizations_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="relative w-full max-w-2xl bg-light-card dark:bg-dark-card rounded-xl shadow-lg p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Bulk Upload Organizations
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Upload a CSV file to create multiple organizations at once.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <button
                onClick={downloadTemplate}
                className="text-sm text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Download CSV Template
              </button>
            </div>

            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-light-hover dark:hover:bg-dark-hover border-light-border dark:border-dark-border">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <CloudArrowUpIcon className="w-10 h-10 mb-3 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">CSV file only</p>
                  {file && (
                    <p className="mt-2 text-sm text-primary-500 dark:text-primary-400">
                      {file.name}
                    </p>
                  )}
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </label>
            </div>

            {error && (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500">
                {error}
              </div>
            )}

            {result && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Successfully created:</span>
                  <span className="font-medium text-green-500">{result.success}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Failed:</span>
                  <span className="font-medium text-red-500">{result.failed}</span>
                </div>
                {result.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Errors:</h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {result.errors.map((error, index) => (
                        <p key={index} className="text-xs text-red-500">
                          {error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className={`px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors duration-200 ${
                (!file || uploading) && 'opacity-50 cursor-not-allowed'
              }`}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOrganizationUpload;